// FlagsPopup — modal that lists a review run's triggered red flags and lets
// the reviewer mark a flag as a false positive (override) with a reason.
// Opened from the Summary tab's clickable aggregate-score / tier card so the
// flags + override controls live in a popup instead of bolted onto the end of
// the summary page. On close, if any flag was overridden during the session,
// the popup asks whether to regenerate the AI summary so it reflects the
// overrides (excludes overridden flags + the recalculated score). Operator
// request 2026-10-26.

import { useEffect, useState } from 'react';
import { X, Flag, RefreshCw, Loader2 } from 'lucide-react';
import type { TriggeredFlag } from '../api/review';
import FlagCard from './FlagCard';

interface FlagsPopupProps {
  open: boolean;
  flags: TriggeredFlag[];
  onOverrideFlag: (flagId: string, reason: string) => Promise<void>;
  onUnoverrideFlag?: (flagId: string) => Promise<void>;
  onRegenerate: () => void;
  regenerating: boolean;
  onClose: () => void;
}

const SEVERITY_ORDER: Record<string, number> = {
  AUTO_ESCALATE: 0,
  HIGH: 1,
  MEDIUM: 2,
  LOW: 3,
};

export default function FlagsPopup({
  open,
  flags,
  onOverrideFlag,
  onUnoverrideFlag,
  onRegenerate,
  regenerating,
  onClose,
}: FlagsPopupProps) {
  const [overridesChanged, setOverridesChanged] = useState(false);
  const [confirmRegenerate, confirmRegenerateOpen] = useState(false);

  // Reset state each time the popup opens.
  useEffect(() => {
    if (open) {
      setOverridesChanged(false);
      confirmRegenerateOpen(false);
    }
  }, [open]);

  if (!open) return null;

  const sortedFlags = [...flags].sort(
    (a, b) =>
      (SEVERITY_ORDER[a.severity] ?? 9) - (SEVERITY_ORDER[b.severity] ?? 9),
  );
  const overriddenCount = sortedFlags.filter(
    (f) => f.override_status === 'overridden',
  ).length;

  // Wrap onOverrideFlag so we can mark that overrides happened (drives the
  // regenerate prompt on close) without coupling FlagCard to popup state.
  const handleOverride = async (flagId: string, reason: string) => {
    await onOverrideFlag(flagId, reason);
    setOverridesChanged(true);
  };

  // Restore an overridden flag (unmark as false positive). Also counts as a
  // change so the regenerate-on-close prompt fires.
  const handleUnoverride = async (flagId: string) => {
    if (!onUnoverrideFlag) return;
    await onUnoverrideFlag(flagId);
    setOverridesChanged(true);
  };

  const requestClose = () => {
    if (overridesChanged && !confirmRegenerate) {
      confirmRegenerateOpen(true);
      return;
    }
    onClose();
  };

  const answerRegenerate = (yes: boolean) => {
    confirmRegenerateOpen(false);
    if (yes) {
      onRegenerate();
    }
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      data-testid="flags-popup"
      onClick={(e) => {
        if (e.target === e.currentTarget) requestClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <Flag size={20} className="text-slate-700" />
            <h2 className="text-lg font-semibold text-slate-800">
              Triggered Red Flags ({sortedFlags.length})
            </h2>
            {overriddenCount > 0 && (
              <span className="ml-2 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                {overriddenCount} marked false positive
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={requestClose}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Close"
            data-testid="flags-popup-close"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="overflow-y-auto p-5 space-y-3">
          {sortedFlags.length === 0 ? (
            <p className="text-sm text-slate-500 py-8 text-center">
              No flags triggered for this review run.
            </p>
          ) : (
            sortedFlags.map((flag) => (
              <FlagCard
                key={flag.id}
                flag={flag}
                onOverride={handleOverride}
                onUnoverride={onUnoverrideFlag ? handleUnoverride : undefined}
              />
            ))
          )}
        </div>

        {/* Footer hint */}
        {overridesChanged && !confirmRegenerate && (
          <div className="px-5 py-3 border-t border-slate-200 text-xs text-slate-500">
            Flags were overridden. Closing will ask whether to regenerate the summary.
          </div>
        )}

        {/* Regenerate confirmation */}
        {confirmRegenerate && (
          <div className="px-5 py-4 border-t border-slate-200 bg-slate-50">
            <p className="text-sm text-slate-700 mb-3">
              You marked flags as false positives. Regenerate the AI summary now
              so it reflects the overrides (excludes dismissed flags + the
              recalculated score)?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={() => answerRegenerate(false)}
                className="btn-secondary text-sm"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={() => answerRegenerate(true)}
                disabled={regenerating}
                className="btn-primary text-sm inline-flex items-center gap-2"
                data-testid="flags-popup-regenerate-yes"
              >
                {regenerating ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <RefreshCw size={14} />
                )}
                Regenerate summary
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}